import React, { useEffect, useMemo, useState } from "react";
import {
  PortalFeedbackDismissModal,
  PortalFeedbackPromptActions,
} from "@bto/shared";
import { useRecoilState, useRecoilValue } from "recoil";
import { useDeviceHook } from "../../utils/hooks/useDeviceHook";
import { getAllRequisitions } from "../../recoil/selectors";
import {
  feedbackModalOpenState,
  feedbackSourceState,
  patientProfileState,
} from "../../recoil/atoms";
import { Banner } from "../../components/Banner/Banner";
import { FeedbackModal } from "../../components/Feedback/FeedbackModal";
import { useBooleanFeatureFlag } from "../../utils/hooks/useFeatureFlag";
import { FeatureFlags } from "../../utils/launchDarklyUtils";

export const FEEDBACK_PROMPT_DISMISSED_KEY = "feedbackPromptDismissed";

export const LandingFeedbackBanner: React.FC = () => {
  const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useRecoilState(
    feedbackModalOpenState,
  );
  const [feedbackSource, setFeedbackSource] =
    useRecoilState(feedbackSourceState);
  const { deviceType } = useDeviceHook();
  const { enabled: isPatientNpsFeedbackEnabled, loading: isFlagLoading } =
    useBooleanFeatureFlag(FeatureFlags.PatientNpsFeedback);
  const patientInfo = useRecoilValue(patientProfileState);
  const requisitions = useRecoilValue(getAllRequisitions);
  const feedbackName = patientInfo?.user?.first_name || "there";
  const bannerTitle = `Hi ${feedbackName}, help us improve our experience by giving us some feedback`;

  const [showBanner, setShowBanner] = useState(false);
  const [isDismissModalOpen, setIsDismissModalOpen] = useState(false);

  const isFeedbackReady = useMemo(() => {
    const isStatusReady = (status?: string) =>
      status !== undefined &&
      status !== "Processing" &&
      status !== "Not Ordered";

    return (
      requisitions?.some((requisition) => {
        const unityStatus = requisition?.unity_reports?.[0]?.status;
        const aneuploidyStatus = requisition?.aneuploidy_reports?.[0]?.status;
        return isStatusReady(unityStatus) || isStatusReady(aneuploidyStatus);
      }) ?? false
    );
  }, [requisitions]);

  useEffect(() => {
    if (isFlagLoading || !requisitions) return;

    const promptDismissed =
      localStorage.getItem(FEEDBACK_PROMPT_DISMISSED_KEY) === "true";

    if (!isPatientNpsFeedbackEnabled) {
      setShowBanner(false);
      setIsFeedbackModalOpen(false);
      setFeedbackSource(null);
      return;
    }

    if (deviceType === "desktop") {
      setShowBanner(false);
      const shouldAutoOpen = isFeedbackReady && !promptDismissed;
      setFeedbackSource(shouldAutoOpen ? "auto" : null);
      setIsFeedbackModalOpen(shouldAutoOpen);
      return;
    }
    setIsFeedbackModalOpen(false);
    setFeedbackSource(null);
    setShowBanner(isFeedbackReady && !promptDismissed);
  }, [
    deviceType,
    isFeedbackReady,
    isFlagLoading,
    isPatientNpsFeedbackEnabled,
    requisitions,
  ]);

  const dismissBanner = () => {
    setShowBanner(false);
    localStorage.setItem(FEEDBACK_PROMPT_DISMISSED_KEY, "true");
    setIsDismissModalOpen(true);
  };

  const handleGiveFeedback = () => {
    setFeedbackSource("auto");
    setIsFeedbackModalOpen(true);
    setShowBanner(false);
  };

  const bannerCTA = (
    <PortalFeedbackPromptActions
      onNotNow={dismissBanner}
      onGiveFeedback={handleGiveFeedback}
    />
  );

  return (
    <>
      {showBanner && deviceType !== "desktop" && isFeedbackReady && (
        <Banner
          bannerType="information"
          bannerTitle={bannerTitle}
          bannerActionCTA={bannerCTA}
        />
      )}
      <FeedbackModal
        isOpen={isFeedbackModalOpen === true}
        onClose={() => {
          setIsFeedbackModalOpen(false);
          setFeedbackSource(null);
        }}
        onDismiss={() =>
          localStorage.setItem(FEEDBACK_PROMPT_DISMISSED_KEY, "true")
        }
        source={feedbackSource ?? "manual"}
      />
      <PortalFeedbackDismissModal
        isOpen={isDismissModalOpen}
        onClose={() => setIsDismissModalOpen(false)}
        bodyMessage="You can find a feedback card at the bottom of the page anytime you feel like giving us feedback."
      />
    </>
  );
};
